import { Box, Button, Card, CardContent, CardHeader, LinearProgress, Typography } from "@mui/material";
import type { ResellerMatchCandidate } from "@xop/dashboard-shared";
import { GitMerge } from "lucide-react";
import type { JSX } from "react";

/**
 * "Is this a reseller we already have?" (spec E §5). The candidates were
 * scored at registration and stored WITH their reasons, so this card can say
 * why each one is here without recomputing. Ranked by score; the counts are
 * context, never score (§5.1: "it is why the reviewer cares").
 *
 * Nothing here links anything. The button opens a confirmation that asks
 * for the role; the reviewer's confirm is the identity decision (§5.2).
 */
export function MatchCandidatesCard({
  candidates,
  canManage,
  blocker,
  onLink,
}: {
  candidates: ResellerMatchCandidate[];
  canManage: boolean;
  /** Why activation is not possible right now, or null. Disables every row's button and is shown beside it. */
  blocker: string | null;
  onLink: (candidate: ResellerMatchCandidate) => void;
}): JSX.Element {
  const ranked = [...candidates].sort((a, b) => b.score - a.score);

  return (
    <Card data-cy="application-candidates">
      <CardHeader
        title={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: "1.125rem", fontWeight: 600 }}>
            <Box sx={{ color: "text.secondary", display: "flex" }}><GitMerge size={20} /></Box>
            <span>Is this a reseller we already have?</span>
          </Box>
        }
        subheader={
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            The system suggests; you decide. Linking gives the applicant access to that reseller&rsquo;s customers and leads.
          </Typography>
        }
      />
      <CardContent sx={{ display: "grid", gap: 1.5 }}>
        {ranked.length === 0 && (
          <Box data-testid="candidates-empty" data-cy="candidates-empty" sx={{ p: 2, bgcolor: "surfaceSoft", borderRadius: 1, fontSize: 14, color: "text.secondary" }}>
            No likely matches. A free-mail address with a generic company name gives the matcher nothing to go on —
            search Administration ▸ Resellers by hand before activating as new.
          </Box>
        )}
        {ranked.map((c, index) => {
          const pct = Math.round(c.score * 100);
          return (
            <Box
              key={c.reseller_id}
              data-testid="candidate-row"
              data-cy="candidate-row"
              data-reseller-id={c.reseller_id}
              sx={{ display: "grid", gap: 1, p: 1.5, bgcolor: "surfaceSoft", borderRadius: 1 }}
            >
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                    <Box component="span" sx={{ color: "text.disabled", mr: 1 }}>#{index + 1}</Box>
                    {c.name}
                    {c.code && (
                      <Box component="span" sx={{ ml: 1, fontSize: 12, fontWeight: 600, color: "secondary.main" }}>{c.code}</Box>
                    )}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                    {c.active_members} {c.active_members === 1 ? "member" : "members"} · {c.customer_count} {c.customer_count === 1 ? "customer" : "customers"} · {c.lead_count} {c.lead_count === 1 ? "lead" : "leads"}
                  </Typography>
                </Box>
                {canManage && (
                  <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                    <Button size="small" variant="contained" disabled={blocker !== null} onClick={() => onLink(c)} data-cy="candidate-activate">
                      Activate as this reseller
                    </Button>
                    {blocker && (
                      <Typography sx={{ fontSize: 11, color: "text.disabled", mt: 0.5, maxWidth: 220 }}>{blocker}</Typography>
                    )}
                  </Box>
                )}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={pct}
                  aria-label={`${pct}% match`}
                  data-testid="candidate-score"
                  data-cy="candidate-score"
                  sx={{ flex: 1, height: 6, borderRadius: 3 }}
                />
                <Typography sx={{ fontSize: 12, color: "text.secondary", width: 40, textAlign: "right" }}>{pct}%</Typography>
              </Box>
              <Box component="ul" sx={{ m: 0, pl: 2.5, fontSize: 12, color: "text.secondary" }}>
                {c.reasons.map((r) => (
                  <li key={r}>{r}</li>
                ))}
              </Box>
            </Box>
          );
        })}
      </CardContent>
    </Card>
  );
}
